import React from 'react';
import { useWatch } from 'react-hook-form';
import type {
  Control,
  FieldErrors,
  FieldValues,
  Path,
  PathValue,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import { VirtualizedFormSelect } from '@roger/ui-components';
import type { KinnserClinicDataResponse } from '../../services/api/types.gen';

/**
 * The External/Internal Referral Source pair, shared by the two push surfaces
 * that can set a referral on a WellSky admission:
 *
 * - the new-patient create-episode form (EMR-1269), where the values ride the
 *   create/onboard payload, and
 * - the scheduling tab for an existing patient whose creation type is
 *   `new_admission` (EMR-1534), where the backend reads them off the intake and
 *   forwards them on the auto-add admission call.
 *
 * Both write the same two intake columns, so the save ordering, the
 * stale-value recovery, and WellSky's external-picks-the-internal behavior live
 * here once rather than being forked per form.
 *
 * Both surfaces put Admission Source on the same line as the pair, passed in
 * as `leadingField` (EMR-1547 on the create-episode form, EMR-1549 on the
 * scheduling tab), so the row is always three columns. `leadingField` is
 * required rather than optional for exactly that reason: a two-column variant
 * would be a layout nothing renders and nothing tests.
 */

/** The fields a host form must carry to render this pair. */
export interface KinnserReferralFormFields {
  kinnser_internal_referral_id?: string | null;
  kinnser_external_referral_id?: string | null;
  kinnser_external_referral_name?: string | null;
}

const INTERNAL_FIELD = 'kinnser_internal_referral_id';
const EXTERNAL_FIELD = 'kinnser_external_referral_id';
// Saved with every external pick so the Intake Manager can show the name
// without the scraped roster. Kept in form state but never rendered, so a
// blur retry of the key can resend it; the server clears it when a key
// change comes without one.
const EXTERNAL_NAME_FIELD = 'kinnser_external_referral_name';

interface KinnserReferralSourceFieldsProps<T extends FieldValues> {
  control: Control<T>;
  register: UseFormRegister<T>;
  setValue: UseFormSetValue<T>;
  errors: FieldErrors<T>;
  handleFieldBlur: (fieldEvent: React.FocusEvent<HTMLElement>) => void;
  saveToServer: (data: Partial<T>) => void;
  clinicData: KinnserClinicDataResponse | null;
  loadingClinicData: boolean;
  /**
   * Whether a referral may be cleared back to "none".
   *
   * True on the new-patient create/onboard surface, where the push builds a
   * fresh admission and an empty field really does mean no referral.
   *
   * False on the existing-patient new-admission surface: Roger Doc overrides a
   * referral only when the request names a truthy value, so an unset field
   * makes WellSky keep the referral carried forward from the prior admission.
   * Offering "clear" there would let a coordinator believe they had removed a
   * referral that the new admission still inherits.
   */
  allowClear?: boolean;
  /**
   * The field rendered as the first cell of the referral row -- Admission
   * Source on both surfaces. Supply a single element that is itself one grid
   * cell. Required: the row is defined as three columns, and the column count
   * must always match the number of cells.
   */
  leadingField: React.ReactNode;
  /**
   * Whether `leadingField` is also unset, for the row's single "left unset"
   * line below. Without it that line would vanish as soon as both referrals
   * were filled, while the leading field was still blank and still governed by
   * the same rule.
   */
  leadingFieldUnset?: boolean;
  /**
   * Suppress the "left unset" line because `leadingField` is already
   * explaining the same outcome in its own notice. One row, one sentence.
   */
  hideUnsetHint?: boolean;
}

export function KinnserReferralSourceFields<
  T extends FieldValues & KinnserReferralFormFields,
>({
  control,
  register,
  setValue,
  errors,
  handleFieldBlur,
  saveToServer,
  clinicData,
  loadingClinicData,
  allowClear = true,
  leadingField,
  leadingFieldUnset = false,
  hideUnsetHint = false,
}: KinnserReferralSourceFieldsProps<T>) {
  // The generic constraint guarantees both keys exist on T, but TypeScript
  // cannot evaluate `Path<T>` for an unresolved T, so the two field names are
  // narrowed once here instead of at every call below.
  const internalName = INTERNAL_FIELD as Path<T>;
  const externalName = EXTERNAL_FIELD as Path<T>;
  const externalNameName = EXTERNAL_NAME_FIELD as Path<T>;
  const referralErrors = errors as FieldErrors<KinnserReferralFormFields>;

  // Referral sources come from the scraped clinic info. The internal option id
  // is the bare WellSky AMUserkeyrs clinic user key; the external option id is
  // the packed "{referral_key}|{type_key}" value, which is never split because
  // Roger Doc matches the submitted value against the live form option exactly.
  const internalReferralSources = React.useMemo(
    () => clinicData?.internal_referral_sources ?? [],
    [clinicData?.internal_referral_sources]
  );
  const externalReferralSources = React.useMemo(
    () => clinicData?.external_referral_sources ?? [],
    [clinicData?.external_referral_sources]
  );
  const externalReferralOptions = React.useMemo(
    () =>
      externalReferralSources.map(source => ({
        id: source.id,
        name: source.name,
      })),
    [externalReferralSources]
  );

  const internalReferralValue =
    (useWatch({ control, name: internalName }) as string | null | undefined) ??
    '';
  const externalReferralValue =
    (useWatch({ control, name: externalName }) as string | null | undefined) ??
    '';

  // Staleness is only knowable once the scraped lists are actually loaded;
  // before that every saved value would look absent from an empty list.
  const clinicDataReady = !loadingClinicData && clinicData !== null;

  // A referral the intake already holds that the re-scraped list no longer
  // offers (the marketer or referral was removed in WellSky). The select is
  // uncontrolled and only displays values that match an option, so this value
  // renders blank while still being what the push would send. Surface it, and
  // keep a way to clear it — with an empty list the select alone is a dead end.
  const staleInternalReferral =
    clinicDataReady &&
    internalReferralValue &&
    !internalReferralSources.some(source => source.id === internalReferralValue)
      ? internalReferralValue
      : null;
  const staleExternalReferral =
    clinicDataReady &&
    externalReferralValue &&
    !externalReferralSources.some(source => source.id === externalReferralValue)
      ? externalReferralValue
      : null;

  // The WellSky default internal referral key of the currently selected
  // external referral, when that key is not in the synced internal list so the
  // auto-select below could not apply it.
  //
  // Derived per render rather than held in state: the warning tells the user to
  // pick an internal referral manually, so it must disappear the moment they do
  // — and the internal select has no change handler of its own to clear a flag
  // from. Deriving it also means an intake reloaded with that external referral
  // still shows the warning. Only the auto-select *mutation* is pick-only;
  // stating a fact about the current selection is not a mutation.
  const missingDefaultInternalKey = React.useMemo(() => {
    if (internalReferralValue) return null;
    const key =
      externalReferralSources.find(
        source => source.id === externalReferralValue
      )?.default_internal_referral_key || '';
    if (!key) return null;
    return internalReferralSources.some(source => source.id === key)
      ? null
      : key;
  }, [
    externalReferralSources,
    externalReferralValue,
    internalReferralSources,
    internalReferralValue,
  ]);

  const clearReferral = (field: Path<T>, key: string) => {
    setValue(field, null as PathValue<T, Path<T>>, { shouldDirty: true });
    saveToServer({ [key]: '' } as Partial<T>);
  };

  const clearExternalReferral = () => {
    setValue(externalName, null as PathValue<T, Path<T>>, {
      shouldDirty: true,
    });
    setValue(externalNameName, null as PathValue<T, Path<T>>);
    saveToServer({
      [EXTERNAL_FIELD]: '',
      [EXTERNAL_NAME_FIELD]: '',
    } as Partial<T>);
  };

  const internalReferralField = register(internalName, {
    onBlur: handleFieldBlur,
  });

  // Persist on the change itself, not on blur. react-select's clear control
  // produces a change with no blur, so a blur-only save drops the clear
  // silently: the UI empties, nothing is sent, and a reload brings the old
  // value back. Saving here also removes the race where a user clears and
  // immediately pushes, with a deferred blur PUT still in flight.
  const handleInternalReferralChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    void internalReferralField.onChange(event);
    saveToServer({
      [INTERNAL_FIELD]: event.target.value || '',
    } as Partial<T>);
  };

  const externalReferralField = register(externalName, {
    onBlur: handleFieldBlur,
  });

  // WellSky's own UI auto-selects an internal referral when an external one is
  // picked. Roger Doc does not replicate that, so it happens here, on a user
  // pick only: form initialization must never mutate a saved intake.
  const handleExternalReferralChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    void externalReferralField.onChange(event);

    const pickedId = event.target.value || '';
    const pickedSource = externalReferralSources.find(
      source => source.id === pickedId
    );
    const defaultInternalKey =
      pickedSource?.default_internal_referral_key || '';
    const pickedName = pickedId ? pickedSource?.name || '' : '';
    setValue(externalNameName, (pickedName || null) as PathValue<T, Path<T>>);
    const externalSave = {
      [EXTERNAL_FIELD]: pickedId,
      [EXTERNAL_NAME_FIELD]: pickedName,
    };

    // No default, or the user cleared the external referral: the internal
    // selection is an independent field from here on and is left alone.
    if (!defaultInternalKey) {
      saveToServer(externalSave as Partial<T>);
      return;
    }

    // Selecting an id that is not among the options would auto-save a value
    // the select can never display. Warn instead, and keep what the user has.
    if (
      !internalReferralSources.some(source => source.id === defaultInternalKey)
    ) {
      // The warning under the internal select derives itself from this state.
      saveToServer(externalSave as Partial<T>);
      return;
    }

    // Deliberately overwrites an existing internal selection, matching what
    // WellSky itself does; the change is on-screen and a later manual pick is
    // never overridden, because this fires only on an external-pick event.
    setValue(internalName, defaultInternalKey as PathValue<T, Path<T>>, {
      shouldDirty: true,
    });
    saveToServer({
      ...externalSave,
      [INTERNAL_FIELD]: defaultInternalKey,
    } as Partial<T>);
  };

  // On a surface that cannot clear, an empty select is not "no referral" — it
  // means WellSky keeps what the prior admission carried. The leading field
  // carries the identical rule, so one line for the whole row says it once:
  // a sentence per field made a coordinator read the same rule three times.
  //
  // Gated on ANY of the three still being unset, so it stays up while the rule
  // still applies to something; and suppressed when the leading field's own
  // notice already states the outcome, so the row never shows two sentences
  // saying one thing.
  const showsUnsetHint =
    !allowClear &&
    clinicDataReady &&
    !hideUnsetHint &&
    (leadingFieldUnset || !internalReferralValue || !externalReferralValue);

  return (
    <>
      {/* External sits before Internal: picking it is what auto-fills the
       internal referral, so the cause reads before the effect. A `leadingField`
       sits ahead of both and makes this a three-column row.

       On the create-episode form this row sits directly above the HH CAHPS
       checkbox, which is the adjacency that produced EMR-1269's original
       blocker: react-select's control is flex-wrap:wrap over a fixed 32px
       height, so in a cell too narrow for the indicator block the indicators
       wrapped to a second line, rendered outside the control, and swallowed the
       checkbox's clicks. Three columns give each control roughly 110px, and at
       react-select's default 8px indicator padding the two indicators take 72px
       of that -- leaving the clear button on the control's geometric centre,
       where a click aimed at the middle wipes the value instead of opening the
       menu. Hence `compactIndicators` on every select in the row.

       Below the md breakpoint the row stacks to one column, because a fraction
       of a 390px viewport would wrap the indicators over the checkbox again.
       Anything that narrows this row must re-check a real click on
       #hhCahpsOptOut at both 1280x720 and 390x844, not just the visual. */}
      <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
        {leadingField}
        <div>
          <VirtualizedFormSelect
            label='External Referral Source'
            placeholder={
              loadingClinicData
                ? 'Loading referral sources...'
                : externalReferralOptions.length === 0
                  ? 'No referral sources synced'
                  : 'Select referral source...'
            }
            options={externalReferralOptions}
            sortOptions
            isClearable={allowClear}
            compactIndicators
            disabled={
              loadingClinicData ||
              (externalReferralOptions.length === 0 && !externalReferralValue)
            }
            error={referralErrors.kinnser_external_referral_id?.message}
            {...externalReferralField}
            onChange={handleExternalReferralChange}
          />
          {staleExternalReferral && (
            <p
              role='status'
              className='mt-1 text-xs text-amber-700'
              data-testid='external-referral-stale-value'
            >
              {`The saved external referral (${staleExternalReferral}) is no longer in the synced list. It will still be sent and the push will fail — pick another`}
              {allowClear ? (
                <>
                  {' or '}
                  <button
                    type='button'
                    className='underline'
                    data-testid='external-referral-clear'
                    onClick={clearExternalReferral}
                  >
                    clear it
                  </button>
                  .
                </>
              ) : (
                '.'
              )}
            </p>
          )}
        </div>

        <div>
          <VirtualizedFormSelect
            label='Internal Referral Source'
            placeholder={
              loadingClinicData
                ? 'Loading referral sources...'
                : internalReferralSources.length === 0
                  ? 'No referral sources synced'
                  : 'Select referral source...'
            }
            options={internalReferralSources}
            sortOptions
            isClearable={allowClear}
            compactIndicators
            // Disabled only when there is nothing to pick AND nothing held.
            // A field holding a value must stay reachable, or a stale value
            // becomes unclearable and the push fails on something the
            // coordinator can neither see nor fix.
            disabled={
              loadingClinicData ||
              (internalReferralSources.length === 0 && !internalReferralValue)
            }
            error={referralErrors.kinnser_internal_referral_id?.message}
            {...internalReferralField}
            onChange={handleInternalReferralChange}
          />
          {staleInternalReferral && (
            <p
              role='status'
              className='mt-1 text-xs text-amber-700'
              data-testid='internal-referral-stale-value'
            >
              {`The saved internal referral (key ${staleInternalReferral}) is no longer in the synced list. It will still be sent and the push will fail — pick another`}
              {allowClear ? (
                <>
                  {' or '}
                  <button
                    type='button'
                    className='underline'
                    data-testid='internal-referral-clear'
                    onClick={() => clearReferral(internalName, INTERNAL_FIELD)}
                  >
                    clear it
                  </button>
                  .
                </>
              ) : (
                '.'
              )}
            </p>
          )}
          {missingDefaultInternalKey && (
            <p
              role='status'
              className='mt-1 text-xs text-amber-700'
              data-testid='internal-referral-default-missing'
            >
              {`WellSky's default internal referral for this selection (key ${missingDefaultInternalKey}) is not in the synced list — pick one manually.`}
            </p>
          )}
        </div>
      </div>
      {showsUnsetHint && (
        <p
          className='mt-1 text-xs text-gray-500'
          data-testid='referral-unset-hint'
        >
          {`Anything left unset keeps the prior admission's value.`}
        </p>
      )}
    </>
  );
}
